type PossibleDateType = string | Date | undefined | null;

export const prepareDate = (date: PossibleDateType): Date | undefined =>
    !date ||
    (typeof date === 'string' && date.startsWith('0001-01-01')) ||
    (typeof date === 'object' &&
        date.toDateString().startsWith('Mon Jan 01 0001'))
        ? undefined
        : new Date(date);

export const considerDateAsUtc = (date?: string | Date): Date | null => {
    if (!date) {
        return null;
    }

    const _date = typeof date === 'string' ? new Date(date) : date;

    return new Date(
        Date.UTC(
            _date.getFullYear(),
            _date.getMonth(),
            _date.getDate(),
            12,
            0,
            0,
        ),
    );
};

const parseMilitaryTime = (
    militaryTime: string,
): { hours: number; minutes: number } => {
    const padded = militaryTime.padStart(4, '0');
    return {
        hours: parseInt(padded.slice(0, 2), 10) % 24,
        minutes: parseInt(padded.slice(2, 4), 10),
    };
};

const isValidMilitaryTime = (
    militaryTime: string | undefined,
): militaryTime is string => {
    if (!militaryTime) return false;

    const { hours, minutes } = parseMilitaryTime(militaryTime);
    return (
        !isNaN(hours) &&
        !isNaN(minutes) &&
        hours >= 0 &&
        hours <= 23 &&
        minutes >= 0 &&
        minutes <= 59
    );
};

export const convertMilitaryTimeTo12Hours = (
    militaryTime: string | undefined,
): string => {
    if (!isValidMilitaryTime(militaryTime)) return '';

    const { hours, minutes } = parseMilitaryTime(militaryTime);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;

    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const buildISODateTimeWithOffset = (
    isoDateString: string,
    time: string,
): string => {
    const datePart = isoDateString.slice(0, 10);
    const offsetPart = isoDateString.slice(19);

    return `${datePart}T${time}:00${offsetPart}`;
};

export const colonTimeToMinutesSinceMidnight = (time: string): number => {
    const stripped = time.replace(':', '').padStart(4, '0');
    const hours = parseInt(stripped.slice(0, 2), 10);
    const minutes = parseInt(stripped.slice(2, 4), 10);

    return hours * 60 + minutes;
};

export const operationOnMilitaryTime = (
    time: string,
    operation: 'add' | 'subtract',
    minutes: number,
    { asColonTime = false }: { asColonTime?: boolean } = {},
): string => {
    if (!isValidMilitaryTime(time)) return '';

    const totalMinutes = colonTimeToMinutesSinceMidnight(time); // Can be used for military time as well since it's just a string of numbers
    const newTotalMinutes =
        operation === 'add' ? totalMinutes + minutes : totalMinutes - minutes;

    const newHours = Math.floor(newTotalMinutes / 60) % 24;
    const newMinutes = newTotalMinutes % 60;

    if (asColonTime) {
        return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
    }

    return `${newHours.toString().padStart(2, '0')}${newMinutes.toString().padStart(2, '0')}`;
};
